/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Suspense, type ReactElement } from 'react';
import { Await } from 'react-router';
import { Link } from '@/components/link';
import { SiFacebook, SiInstagram, SiX, SiYoutube } from '@icons-pack/react-simple-icons';
import type { ShopperProducts } from '@salesforce/storefront-next-runtime/scapi';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { UITarget } from '@/targets/ui-target';
import { useTranslation } from 'react-i18next';
import LocaleSwitcher from '@/components/locale-switcher';
import CurrencySwitcher from '@/components/currency-switcher';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { CONFIDENTIALITY_NOTICE_HREF, TERMS_OF_USE_HREF } from '@/lib/main-site-legal-hrefs';
import { cn } from '@/lib/utils';
interface MainFooterProps {
    categories?: Promise<ShopperProducts.schemas['Category']>;
}

const footerLinkClassName =
    'group/footer-link inline-flex text-xs text-current hover:text-current focus:text-current transition-colors';
const footerLinkTextClassName =
    'relative w-fit origin-center transition-transform after:absolute after:left-0 after:bottom-0 after:h-px after:w-full after:origin-left after:scale-x-0 after:bg-current after:transition-transform group-hover/footer-link:-rotate-3 group-hover/footer-link:after:scale-x-100 group-focus/footer-link:-rotate-3 group-focus/footer-link:after:scale-x-100';

export default function MainFooter({ categories }: MainFooterProps): ReactElement {
    const { t } = useTranslation('footer');
    const config = useConfig();
    const b2bEnabled = config.features.b2b.enabled;
    return (
        <footer className="mt-auto">
            {/* Footer Links Section */}
            <div className="bg-footer-background text-footer-foreground p-4 lg:p-6 section-container">
                <div className="lg:max-w-[407px]">
                    <Accordion type="multiple" className="grid grid-cols border-current">
                        {!b2bEnabled ? (
                            <AccordionItem value="shop" className="last:border-b">
                                <AccordionTrigger className="text-left text-base font-semibold hover:no-underline">
                                    {t('sections.shop')}
                                </AccordionTrigger>
                                <AccordionContent>
                                    <ul>
                                        <UITarget targetId="sfcc.footer.customersupport.start" />
                                        {categories ? (
                                            <Suspense fallback={null}>
                                                <Await resolve={categories} errorElement={null}>
                                                    {(rootCategory) => (
                                                        <>
                                                            {rootCategory.categories?.map((category) => (
                                                                <li key={category.id} className="mb-1 last:mb-0">
                                                                    <Link
                                                                        to={`/category/${category.id}`}
                                                                        className={cn(footerLinkClassName, 'text-base')}>
                                                                        <span className={footerLinkTextClassName}>
                                                                            {category.name}
                                                                        </span>
                                                                    </Link>
                                                                </li>
                                                            ))}
                                                        </>
                                                    )}
                                                </Await>
                                            </Suspense>
                                        ) : null}
                                        <UITarget targetId="sfcc.footer.customersupport.end" />
                                    </ul>
                                </AccordionContent>
                            </AccordionItem>
                        ) : null}

                        {/* Accordion 2: Help */}
                        <AccordionItem value="help" className="last:border-b">
                            <AccordionTrigger className="text-left text-base font-semibold hover:no-underline">
                                {t('sections.help')}
                            </AccordionTrigger>
                            <AccordionContent>
                                <ul>
                                    <UITarget targetId="sfcc.footer.account.start" />
                                    {b2bEnabled ? (
                                        <>
                                            <li>
                                                <Link
                                                    to="/page/contact-us"
                                                    className={cn(footerLinkClassName, 'text-base')}>
                                                    <span className={footerLinkTextClassName}>
                                                        {t('links.contactUs')}
                                                    </span>
                                                </Link>
                                            </li>
                                            <li className="mt-1">
                                                <Link
                                                    to="/page/faq"
                                                    className={cn(footerLinkClassName, 'text-base')}>
                                                    <span className={footerLinkTextClassName}>{t('links.faq')}</span>
                                                </Link>
                                            </li>
                                        </>
                                    ) : (
                                        <>
                                            <li className="mb-1">
                                                <Link
                                                    to="/page/contact-us"
                                                    className={cn(footerLinkClassName, 'text-base')}>
                                                    <span className={footerLinkTextClassName}>
                                                        {t('links.contactUs')}
                                                    </span>
                                                </Link>
                                            </li>
                                            <li className="mb-1">
                                                <Link to="/shipping" className={cn(footerLinkClassName, 'text-base')}>
                                                    <span className={footerLinkTextClassName}>
                                                        {t('links.shipping')}
                                                    </span>
                                                </Link>
                                            </li>
                                            <li className="mb-1">
                                                <Link to="/orders" className={cn(footerLinkClassName, 'text-base')}>
                                                    <span className={footerLinkTextClassName}>
                                                        {t('links.orderStatus')}
                                                    </span>
                                                </Link>
                                            </li>
                                            <li>
                                                <Link to="/login" className={cn(footerLinkClassName, 'text-base')}>
                                                    <span className={footerLinkTextClassName}>
                                                        {t('links.signInOrCreateAccount')}
                                                    </span>
                                                </Link>
                                            </li>
                                        </>
                                    )}
                                    <UITarget targetId="sfcc.footer.account.end" />
                                </ul>
                            </AccordionContent>
                        </AccordionItem>

                        {/* Accordion 3: About */}
                        <AccordionItem value="about" className="last:border-b">
                            <AccordionTrigger className="text-left text-base font-semibold hover:no-underline">
                                {t('sections.about')}
                            </AccordionTrigger>
                            <AccordionContent>
                                <ul>
                                    <UITarget targetId="sfcc.footer.ourcompany.start" />
                                    <li className="mb-1">
                                        <Link
                                            to="/page/about-the-tool"
                                            className={cn(footerLinkClassName, 'text-base')}>
                                            <span className={footerLinkTextClassName}>{t('links.aboutTheTool')}</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/about-us" className={cn(footerLinkClassName, 'text-base')}>
                                            <span className={footerLinkTextClassName}>{t('links.aboutUs')}</span>
                                        </Link>
                                    </li>
                                    {b2bEnabled ? (
                                        <li className="mt-1">
                                            <Link to="/news" className={cn(footerLinkClassName, 'text-base')}>
                                                <span className={footerLinkTextClassName}>{t('links.news')}</span>
                                            </Link>
                                        </li>
                                    ) : null}
                                    <UITarget targetId="sfcc.footer.ourcompany.end" />
                                </ul>
                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>

                    {/* Footer Bottom */}
                    <div className={cn(b2bEnabled ? 'mt-4' : 'mt-4 lg:mt-12 lg:pt-8')}>
                        <div className="flex flex-col">
                            {/* Social Media Icons */}
                            <div className="flex items-center gap-1">
                                <a
                                    href="https://youtube.com/channel/UCSTGHqzR1Q9yAVbiS3dAFHg"
                                    aria-label={t('socialMedia.youtubeLabel')}
                                    className="p-2">
                                    <SiYoutube className="w-3 h-3" />
                                </a>
                                <a
                                    href="https://instagram.com/commercecloud"
                                    aria-label={t('socialMedia.instagramLabel')}
                                    className="p-2">
                                    <SiInstagram className="w-3 h-3" />
                                </a>
                                <a
                                    href="https://x.com/CommerceCloud"
                                    aria-label={t('socialMedia.xLabel')}
                                    className="p-2">
                                    <SiX className="w-3 h-3" />
                                </a>
                                <a
                                    href="https://facebook.com/CommerceCloud/"
                                    aria-label={t('socialMedia.facebookLabel')}
                                    className="p-2">
                                    <SiFacebook className="w-3 h-3" />
                                </a>
                            </div>
                            {!b2bEnabled ? (
                                <div className="flex items-center gap-1 mb-6">
                                    <LocaleSwitcher />
                                    <CurrencySwitcher />
                                </div>
                            ) : null}

                            {/* Policy Links */}
                            <div className="flex flex-wrap gap-1 text-sm">
                                {b2bEnabled ? (
                                    <>
                                        <Link to={CONFIDENTIALITY_NOTICE_HREF} className="underline">
                                            {t('links.confidentialityNotice')}
                                        </Link>
                                        <Link to={TERMS_OF_USE_HREF} className="underline">
                                            {t('links.termsOfUse')}
                                        </Link>
                                    </>
                                ) : (
                                    <>
                                        <Link to="/accessibility" className="underline">
                                            {t('links.accessibility')}
                                        </Link>
                                        <Link to={CONFIDENTIALITY_NOTICE_HREF} className="underline">
                                            {t('links.confidentialityNotice')}
                                        </Link>
                                        <Link to="/privacy-choices" className="underline">
                                            {t('links.privacyChoices')}
                                        </Link>
                                        <Link to={TERMS_OF_USE_HREF} className="underline">
                                            {t('links.termsOfUse')}
                                        </Link>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
}
